import { useState, useEffect } from 'react'
import { X, Edit2, Check, AlertCircle } from 'lucide-react'
import axios from 'axios'
import useAuthStore from '../store/auth-store'
import { ChangePasswordModal } from './ChangePasswordModal'
import { SecureInput } from './SecureInput'
import { scanInput } from '../lib/inputSecurity'

export function AccountModal({ isOpen, onClose }) {
  const { user, updateUser } = useAuthStore()
  const [isEditMode, setIsEditMode] = useState(false)
  const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState(null)
  const [messageType, setMessageType] = useState('success')
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    company: '',
    street_address: '',
    city: '',
    state_province: '',
    postal_code: '',
    country: 'US',
    timezone: '(UTC+00:00) UTC',
    email: '',
    phone: ''
  })

  // Get member since date
  const getMemberSinceDate = () => {
    if (user?.date_joined) {
      const date = new Date(user.date_joined)
      return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })
    }
    return 'N/A'
  }

  // Initialize form with user data
  useEffect(() => {
    if (user) {
      setFormData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        company: user.company || '',
        street_address: user.street_address || '',
        city: user.city || '',
        state_province: user.state_province || '',
        postal_code: user.postal_code || '',
        country: user.country || 'US',
        timezone: user.timezone || '(UTC+00:00) UTC',
        email: user.email || '',
        phone: user.phone || ''
      })
    }
  }, [user, isOpen])

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleSave = async () => {
    setMessage(null)
    const secErr = scanInput(formData.first_name) || scanInput(formData.last_name) ||
      scanInput(formData.company) || scanInput(formData.street_address) ||
      scanInput(formData.city) || scanInput(formData.state_province) || scanInput(formData.postal_code)
    if (secErr) { setMessage({ type: 'error', text: secErr }); return }
    setLoading(true)

    try {
      const API_URL = import.meta.env.VITE_API_URL || 'https://wqlive.ezxdemo.com'
      const API_BASE_PATH = import.meta.env.VITE_API_BASE_PATH || '/api'
      const JWT_STORAGE_KEY = import.meta.env.VITE_JWT_STORAGE_KEY || 'auth_token'
      const token = (localStorage.getItem(JWT_STORAGE_KEY) || sessionStorage.getItem(JWT_STORAGE_KEY))

      if (!token) {
        throw new Error('Authentication token not found. Please login again.')
      }

      const response = await axios.put(
        `${API_URL}${API_BASE_PATH}/account/profile/`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      )

      const updatedUser = response.data.user || response.data
      updateUser(updatedUser)
      
      setMessageType('success')
      setMessage('Profile updated successfully!')
      setIsEditMode(false)
      
      setTimeout(() => {
        setMessage(null)
      }, 3000)
    } catch (error) {
      setMessageType('error')
      let errorMessage = 'Failed to save profile. Please try again.'
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message
      } else if (error.message) {
        errorMessage = error.message
      }
      setMessage(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-xl w-full max-h-[85vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-start justify-between p-8 border-b border-gray-100 sticky top-0 bg-white">
          <div className="flex-1">
            <h2 className="text-2xl font-bold text-gray-900">
              {isEditMode ? 'Edit Account Summary' : 'Account Summary'}
            </h2>
            <p className="text-sm text-gray-500 mt-1">Member Since {getMemberSinceDate()}</p>
          </div>
          <div className="flex items-center gap-3 ml-4">
            {!isEditMode && (
              <button
                onClick={() => setIsEditMode(true)}
                className="p-2 hover:bg-gray-100 rounded-lg transition"
                title="Edit"
              >
                <Edit2 className="w-5 h-5 text-gray-600" />
              </button>
            )}
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition"
              title="Close"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-8">
          {/* Message */}
          {message && (
            <div className={`mb-6 p-4 rounded-lg text-sm flex items-center gap-2 ${
              messageType === 'success' 
                ? 'bg-green-50 border border-green-200 text-green-800' 
                : 'bg-red-50 border border-red-200 text-red-800'
            }`}>
              {messageType === 'success' ? (
                <Check className="w-4 h-4 flex-shrink-0" />
              ) : (
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
              )}
              {message}
            </div>
          )}

          {isEditMode ? (
            // Edit Mode
            <div className="space-y-4">
              {/* Name Fields */}
              <div className="grid grid-cols-2 gap-4">
                <SecureInput type="text" name="first_name" value={formData.first_name} onChange={handleInputChange} placeholder="First Name" className="w-full px-3 py-2 border border-gray-300 rounded-md text-gray-900 text-sm placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500" />
                <SecureInput type="text" name="last_name" value={formData.last_name} onChange={handleInputChange} placeholder="Last Name" className="w-full px-3 py-2 border border-gray-300 rounded-md text-gray-900 text-sm placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500" />
              </div>

              {/* Company */}
              <SecureInput type="text" name="company" value={formData.company} onChange={handleInputChange} placeholder="Company" className="w-full px-3 py-2 border border-gray-300 rounded-md text-gray-900 text-sm placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500" />

              {/* Address */}
              <SecureInput type="text" name="street_address" value={formData.street_address} onChange={handleInputChange} placeholder="Street Address" className="w-full px-3 py-2 border border-gray-300 rounded-md text-gray-900 text-sm placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500" />

              {/* City, State, Zip */}
              <div className="grid grid-cols-3 gap-3">
                <SecureInput type="text" name="city" value={formData.city} onChange={handleInputChange} placeholder="City" className="w-full px-3 py-2 border border-gray-300 rounded-md text-gray-900 text-sm placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500" />
                <SecureInput type="text" name="state_province" value={formData.state_province} onChange={handleInputChange} placeholder="State" className="w-full px-3 py-2 border border-gray-300 rounded-md text-gray-900 text-sm placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500" />
                <SecureInput type="text" name="postal_code" value={formData.postal_code} onChange={handleInputChange} placeholder="Zip" className="w-full px-3 py-2 border border-gray-300 rounded-md text-gray-900 text-sm placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500" />
              </div>

              {/* Country & Timezone */}
              <div className="grid grid-cols-2 gap-4">
                <select
                  name="country"
                  value={formData.country}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-gray-900 text-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 bg-white"
                >
                  <option value="US">United States</option>
                  <option value="CA">Canada</option>
                  <option value="UK">United Kingdom</option>
                  <option value="AU">Australia</option>
                  <option value="AF">Afghanistan</option>
                </select>
                <select
                  name="timezone"
                  value={formData.timezone}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-gray-900 text-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 bg-white"
                >
                  <option value="(UTC-12:00) International Date Line West">(UTC-12:00) International Date Line West</option>
                  <option value="(UTC-11:00) Midway Island, Samoa">(UTC-11:00) Midway Island, Samoa</option>
                  <option value="(UTC-10:00) Hawaii">(UTC-10:00) Hawaii</option>
                  <option value="(UTC-09:00) Alaska">(UTC-09:00) Alaska</option>
                  <option value="(UTC-08:00) Pacific Time (US & Canada)">(UTC-08:00) Pacific Time (US & Canada)</option>
                  <option value="(UTC-07:00) Mountain Time (US & Canada)">(UTC-07:00) Mountain Time (US & Canada)</option>
                  <option value="(UTC-06:00) Central Time (US & Canada)">(UTC-06:00) Central Time (US & Canada)</option>
                  <option value="(UTC-05:00) Eastern Time (US & Canada)">(UTC-05:00) Eastern Time (US & Canada)</option>
                  <option value="(UTC+00:00) UTC">(UTC+00:00) UTC</option>
                  <option value="(UTC+01:00) London, Paris, Berlin">(UTC+01:00) London, Paris, Berlin</option>
                  <option value="(UTC+05:30) India Standard Time">(UTC+05:30) India Standard Time</option>
                </select>
              </div>

              {/* Email */}
              <input
                type="email"
                name="email"
                value={formData.email}
                disabled
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-gray-600 bg-gray-50 text-sm cursor-not-allowed"
              />

              {/* Phone */}
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                placeholder="Phone Number (For 2FA)"
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-gray-900 text-sm placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              />

              {/* Change Password Link */}
              <div className="pt-2">
                <button
                  onClick={() => {
                    setIsEditMode(false)
                    setIsChangePasswordModalOpen(true)
                  }}
                  className="text-blue-600 hover:text-blue-800 text-sm"
                >
                  Change Password
                </button>
              </div>
            </div>
          ) : (
            // View Mode
            <div className="space-y-6">
              <div>
                <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wide">Name</h3>
                <p className="text-base text-gray-900 mt-2">
                  {formData.first_name} {formData.last_name}
                </p>
              </div>

              {formData.company && (
                <div>
                  <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wide">Company</h3>
                  <p className="text-base text-gray-900 mt-2">{formData.company}</p>
                </div>
              )}

              {(formData.street_address || formData.city || formData.state_province || formData.postal_code || formData.country) && (
                <div>
                  <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wide">Address</h3>
                  <p className="text-base text-gray-900 mt-2 leading-relaxed">
                    {formData.street_address && <>{formData.street_address}<br /></>}
                    {formData.city || formData.state_province || formData.postal_code ? (
                      <>
                        {formData.city}{formData.state_province && `, ${formData.state_province}`} {formData.postal_code}
                        <br />
                      </>
                    ) : null}
                    {formData.country}
                  </p>
                </div>
              )}

              {formData.timezone && (
                <div>
                  <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wide">Time Zone</h3>
                  <p className="text-base text-gray-900 mt-2">{formData.timezone}</p>
                </div>
              )}

              {formData.email && (
                <div>
                  <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wide">Email</h3>
                  <p className="text-base text-gray-900 mt-2">{formData.email}</p>
                </div>
              )}

              <div>
                <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wide">Phone (For 2FA)</h3>
                <p className="text-base text-gray-900 mt-2">{formData.phone || '—'}</p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {isEditMode && (
          <div className="flex gap-3 p-6 border-t border-gray-200 bg-gray-50">
            <button
              onClick={() => setIsEditMode(false)}
              disabled={loading}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-100 transition disabled:bg-gray-100"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={loading}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition disabled:bg-blue-400 font-semibold"
            >
              {loading ? 'Saving...' : 'SAVE CHANGES'}
            </button>
          </div>
        )}
      </div>

      {/* Change Password Modal */}
      <ChangePasswordModal 
        isOpen={isChangePasswordModalOpen} 
        onClose={() => setIsChangePasswordModalOpen(false)}
      />
    </div>
  )
}
